import { useState } from 'react';

interface Product {
  id: string;
  code: string;
  name: string;
  category: string;
  price: number;
  stock: number;
  barcode: string;
  unit: string;
  image: string;
}

interface ProductSearchModalProps {
  onClose: () => void;
  onAddProduct: (product: Product, quantity: number) => void;
}

export default function ProductSearchModal({ onClose, onAddProduct }: ProductSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quantities, setQuantities] = useState<Record<string, string>>({});

  const categories = [
    { id: 'all', name: 'All Categories', icon: 'ri-apps-line' },
    { id: 'produce', name: 'Produce', icon: 'ri-leaf-line' },
    { id: 'dairy', name: 'Dairy', icon: 'ri-cup-line' },
    { id: 'bakery', name: 'Bakery', icon: 'ri-cake-3-line' },
    { id: 'meat', name: 'Meat & Seafood', icon: 'ri-restaurant-line' },
    { id: 'beverages', name: 'Beverages', icon: 'ri-goblet-line' },
    { id: 'pantry', name: 'Pantry', icon: 'ri-archive-line' },
  ];

  const allProducts: Product[] = [
    { id: 'P001', code: 'MLK-001', name: 'Organic Whole Milk 1L', category: 'dairy', price: 4.99, stock: 156, barcode: '8901234567890', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=organic%20whole%20milk%20bottle%20in%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=mlk001&orientation=squarish' },
    { id: 'P002', code: 'BRD-001', name: 'Fresh Bread Whole Wheat', category: 'bakery', price: 3.49, stock: 89, barcode: '8901234567891', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=fresh%20whole%20wheat%20bread%20loaf%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=brd001&orientation=squarish' },
    { id: 'P003', code: 'COF-001', name: 'Premium Coffee Beans 500g', category: 'beverages', price: 12.99, stock: 45, barcode: '8901234567892', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=premium%20coffee%20beans%20bag%20package%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=cof001&orientation=squarish' },
    { id: 'P004', code: 'YOG-001', name: 'Greek Yogurt 500g', category: 'dairy', price: 5.49, stock: 234, barcode: '8901234567893', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=greek%20yogurt%20container%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=yog001&orientation=squarish' },
    { id: 'P005', code: 'BAN-001', name: 'Organic Bananas', category: 'produce', price: 2.99, stock: 450, barcode: '8901234567894', unit: 'kg', image: 'https://readdy.ai/api/search-image?query=fresh%20organic%20bananas%20bunch%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=ban001&orientation=squarish' },
    { id: 'P006', code: 'EGG-001', name: 'Free Range Eggs (12 pack)', category: 'dairy', price: 6.99, stock: 178, barcode: '8901234567895', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=free%20range%20eggs%20carton%20package%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=egg001&orientation=squarish' },
    { id: 'P007', code: 'CHE-001', name: 'Cheddar Cheese 200g', category: 'dairy', price: 4.79, stock: 123, barcode: '8901234567896', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=cheddar%20cheese%20block%20package%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=che001&orientation=squarish' },
    { id: 'P008', code: 'JUI-001', name: 'Orange Juice 1L', category: 'beverages', price: 4.99, stock: 267, barcode: '8901234567897', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=orange%20juice%20bottle%20carton%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=jui001&orientation=squarish' },
    { id: 'P009', code: 'PAS-001', name: 'Pasta Penne 500g', category: 'pantry', price: 2.49, stock: 345, barcode: '8901234567898', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=penne%20pasta%20package%20bag%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=pas001&orientation=squarish' },
    { id: 'P010', code: 'SAU-001', name: 'Tomato Sauce 400g', category: 'pantry', price: 1.99, stock: 456, barcode: '8901234567899', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=tomato%20sauce%20can%20jar%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=sau001&orientation=squarish' },
    { id: 'P011', code: 'SPI-001', name: 'Fresh Spinach 250g', category: 'produce', price: 3.29, stock: 89, barcode: '8901234567900', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=fresh%20spinach%20leaves%20package%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=spi001&orientation=squarish' },
    { id: 'P012', code: 'CHK-001', name: 'Chicken Breast', category: 'meat', price: 12.99, stock: 67, barcode: '8901234567901', unit: 'kg', image: 'https://readdy.ai/api/search-image?query=fresh%20chicken%20breast%20meat%20package%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=chk001&orientation=squarish' },
    { id: 'P013', code: 'APP-001', name: 'Red Apples', category: 'produce', price: 3.99, stock: 234, barcode: '8901234567902', unit: 'kg', image: 'https://readdy.ai/api/search-image?query=fresh%20red%20apples%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=app001&orientation=squarish' },
    { id: 'P014', code: 'RIC-001', name: 'Basmati Rice 5kg', category: 'pantry', price: 15.99, stock: 78, barcode: '8901234567903', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=basmati%20rice%20bag%20package%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=ric001&orientation=squarish' },
    { id: 'P015', code: 'BUT-001', name: 'Salted Butter 250g', category: 'dairy', price: 4.29, stock: 145, barcode: '8901234567904', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=salted%20butter%20package%20block%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=but001&orientation=squarish' },
    { id: 'P016', code: 'TOM-001', name: 'Cherry Tomatoes 500g', category: 'produce', price: 4.49, stock: 167, barcode: '8901234567905', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=cherry%20tomatoes%20package%20container%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=tom001&orientation=squarish' },
    { id: 'P017', code: 'SAL-001', name: 'Atlantic Salmon Fillet', category: 'meat', price: 18.99, stock: 34, barcode: '8901234567906', unit: 'kg', image: 'https://readdy.ai/api/search-image?query=atlantic%20salmon%20fillet%20package%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=sal001&orientation=squarish' },
    { id: 'P018', code: 'TEA-001', name: 'Green Tea Bags 100ct', category: 'beverages', price: 8.99, stock: 89, barcode: '8901234567907', unit: 'ea', image: 'https://readdy.ai/api/search-image?query=green%20tea%20bags%20box%20package%20on%20clean%20white%20background%20product%20photography%20minimalist%20style%20simple%20backdrop%20professional%20lighting%20high%20quality%20commercial%20photo&width=200&height=200&seq=tea001&orientation=squarish' },
  ];

  const filteredProducts = allProducts.filter(product => {
    const matchesSearch = searchQuery === '' || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.barcode.includes(searchQuery);
    
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleAddToBasket = () => {
    if (selectedProduct) {
      const qty = parseFloat(quantities[selectedProduct.id] || '1');
      if (qty > 0) {
        onAddProduct(selectedProduct, qty);
        onClose();
      }
    }
  };

  const updateQuantity = (productId: string, value: string) => {
    setQuantities(prev => ({ ...prev, [productId]: value }));
  };

  const getQuantity = (productId: string) => {
    return quantities[productId] || '1';
  };

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <i className="ri-search-line text-teal-600 text-xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 whitespace-nowrap">Product Search</h2>
              <p className="text-sm text-gray-600 whitespace-nowrap">Browse and add products to basket</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl text-gray-600"></i>
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-gray-200 space-y-4">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, code, or barcode..."
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500"
              autoFocus
            />
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Category:</span>
              <div className="flex gap-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      selectedCategory === cat.id
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-4 gap-4">
            {filteredProducts.map(product => {
              const isSelected = selectedProduct?.id === product.id;
              const qty = quantities[product.id] || '1';
              const lineTotal = product.price * parseFloat(qty);

              return (
                <div
                  key={product.id}
                  onClick={() => handleSelectProduct(product)}
                  className={`relative bg-white border-2 rounded-xl overflow-hidden transition-all cursor-pointer ${
                    isSelected
                      ? 'border-teal-500 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  {/* Product Image */}
                  <div className="w-full h-48 bg-gray-100 overflow-hidden relative">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover object-top"
                    />
                    
                    {/* Overlay with Quantity and Add Button - Only show when selected */}
                    {isSelected && (
                      <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
                        <div className="w-full space-y-3">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 text-center whitespace-nowrap">
                              Quantity
                            </label>
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => updateQuantity(product.id, Math.max(0.1, parseFloat(qty) - (product.unit === 'kg' ? 0.1 : 1)).toFixed(1))}
                                className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-bold transition-colors cursor-pointer flex-shrink-0"
                              >
                                <i className="ri-subtract-line text-lg"></i>
                              </button>
                              <input
                                type="number"
                                value={qty}
                                onChange={(e) => updateQuantity(product.id, e.target.value)}
                                className="w-24 px-3 py-2.5 text-center border-2 border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 text-lg font-bold"
                                step={product.unit === 'kg' ? '0.1' : '1'}
                                min="0.1"
                              />
                              <button
                                onClick={() => updateQuantity(product.id, (parseFloat(qty) + (product.unit === 'kg' ? 0.1 : 1)).toFixed(1))}
                                className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-bold transition-colors cursor-pointer flex-shrink-0"
                              >
                                <i className="ri-add-line text-lg"></i>
                              </button>
                            </div>
                          </div>

                          <button
                            onClick={() => handleAddToBasket()}
                            className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-bold transition-colors cursor-pointer whitespace-nowrap text-sm"
                          >
                            <i className="ri-shopping-cart-line mr-2"></i>
                            Add to Basket
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono whitespace-nowrap">
                        {product.code}
                      </span>
                      <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium capitalize whitespace-nowrap">
                        {product.category}
                      </span>
                    </div>

                    <h3 className="text-sm font-bold text-gray-900 line-clamp-2 min-h-[2.5rem]">
                      {product.name}
                    </h3>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <i className="ri-stack-line"></i>
                        <span className="whitespace-nowrap">Stock: {product.stock}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900 whitespace-nowrap">
                          ${product.price.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500 whitespace-nowrap">per {product.unit}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredProducts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-lg font-semibold text-gray-600 mb-1">No products found</p>
              <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
